/*
 * ******************************************************************
 * Copyright (c) 2021-2025 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */

import { Observable } from "rxjs";
import { Injectable } from "@angular/core";
import { HttpWrapperService, RbacService } from "@app/services";
import { UiPrivilegeEnum } from "./roles.model";
import { HttpErrorResponse } from "@angular/common/http";

type HttpFunc<T> = ((string, boolean?) => Observable<T>) | ((string, any?, boolean?) => Observable<T>);
type HttpFuncResult<T> = (...args) => Observable<T>;

export class RbacError extends HttpErrorResponse {
    constructor(public privilege: UiPrivilegeEnum) {
        super({});
    }
}

// @formatter:off
@Injectable({
    providedIn: "root"
})
// @formatter:on
export class RbacHttpService {
    constructor(private http: HttpWrapperService,
                private rbacService: RbacService) {
    }

    /**
     * Return a function that conditionally checks that the user has the exact required privilege before executing
     * an HTTP GET request.
     *
     * @param privilege : UiPrivilegeEnum
     */
    public get<T>(privilege: UiPrivilegeEnum): HttpFuncResult<T> {
        return this.httpWrapper<T>(this.http.get, privilege);
    }

    /**
     * Return a function that conditionally checks that the user has the exact required privilege before executing
     * an HTTP POST request.
     *
     * @param privilege : UiPrivilegeEnum
     */
    public post<T>(privilege: UiPrivilegeEnum): HttpFuncResult<T> {
        return this.httpWrapper<T>(this.http.post, privilege);
    }

    /**
     * Return a function that conditionally checks that the user has the exact required privilege before executing
     * an HTTP PATCH request.
     *
     * @param privilege : UiPrivilegeEnum
     */
    public patch<T>(privilege: UiPrivilegeEnum): HttpFuncResult<T> {
        return this.httpWrapper<T>(this.http.patch, privilege);
    }

    /**
     * Return a function that conditionally checks that the user has the exact required privilege before executing
     * an HTTP DELETE request.
     *
     * @param privilege : UiPrivilegeEnum
     */
    public delete<T>(privilege: UiPrivilegeEnum): HttpFuncResult<T> {
        return this.httpWrapper<T>(this.http.delete, privilege);
    }

    /**
     * Return a wrapper function for any HttpClient function (get, post, delete, etc.). If the user does not have
     * the required privilege, the function will return an Observable with an error result.
     *
     * @private
     * @param httpFunc : Function - HttpClient function
     * @param privilege : UiPrivilegeEnum
     */
    private httpWrapper<T>(httpFunc: HttpFunc<T>, privilege: UiPrivilegeEnum): HttpFuncResult<T> {
        const canRequest: boolean = (!privilege || this.rbacService.hasPrivilege(privilege));
        const httpContext: HttpWrapperService = this.http;
        return function(...args): Observable<T> {
            if (!canRequest) {
                const errorObservable: Observable<T> = new Observable<any>(subscriber => {
                    subscriber.error(new RbacError(privilege));
                });
                return errorObservable;
            }
            return httpFunc.apply(httpContext, args);
        };
    }
}
